import {
	BuyerOffer,
	CategorizedProperties,
	DashboardItem,
	DashboardTab,
	PropertyDetails,
	SuggestedProperty,
} from "@/types/buyer-dashboard";
import { BuyerProject } from "@/types/buyerProject";
import { BuyerUnit } from "@/types/buyerUnit";
import { ProcessDetails } from "@/types/processDetails";

/**
 * Categorizes a buyer unit or project based on its process details
 */
export function categorizeItem(item: BuyerUnit | BuyerProject, categories: CategorizedProperties): void {
	const hasShortlisted = item.processDetails?.some(
		(pd: ProcessDetails) => pd.process === "shortlisting" && pd.stage === "done",
	);
	const hasVisited = item.processDetails?.some((pd: ProcessDetails) => pd.process === "visit" && pd.stage === "done");
	const hasInterested = item.processDetails?.some(
		(pd: ProcessDetails) => pd.process === "decision" && pd.stage === "done",
	);
	const hasRejected = item.cancellation?.flag === true;

	if (hasRejected) {
		categories.rejected.push(item as DashboardItem);
	} else if (hasInterested) {
		categories.interested.push(item as DashboardItem);
	} else if (hasVisited) {
		categories.visited.push(item as DashboardItem);
	} else if (hasShortlisted) {
		categories.shortlisted.push(item as DashboardItem);
	} else {
		categories.suggested.push(item as DashboardItem);
	}
}

/**
 * Extract unit ID from a dashboard item
 */
export function getUnitId(item: DashboardItem): string | undefined {
	return (item as SuggestedProperty).unitId || (item as BuyerUnit).unit?.id;
}

/**
 * Extract project ID from a dashboard item
 */
export function getProjectId(item: DashboardItem): string | undefined {
	return (item as SuggestedProperty).projectId || (item as BuyerProject).project?.id;
}

/**
 * Extract IDs from categorized properties for a specific tab
 */
export function extractActiveIds(
	categorizedData: CategorizedProperties,
	activeTab: DashboardTab,
): { unitIds: string[]; projectIds: string[] } {
	const activeItems = categorizedData[activeTab] || [];
	const unitIds = new Set<string>();
	const projectIds = new Set<string>();

	activeItems.forEach(item => {
		const unitId = getUnitId(item);
		const projectId = getProjectId(item);
		if (unitId) unitIds.add(unitId);
		if (projectId) projectIds.add(projectId);
	});

	return {
		unitIds: Array.from(unitIds),
		projectIds: Array.from(projectIds),
	};
}

/**
 * Merges property details from multiple SUPs for a project
 */
export function mergeProjectDetails(sups: PropertyDetails[]): PropertyDetails {
	if (sups.length === 0) throw new Error("Cannot merge empty SUP array");
	if (sups.length === 1) return sups[0];

	return {
		...sups[0],
		bedrooms: sups.flatMap(d => d.bedrooms),
		bathrooms: sups.flatMap(d => d.bathrooms),
		halls: sups.flatMap(d => d.halls),
		kitchens: sups.flatMap(d => d.kitchens),
		balconies: sups.flatMap(d => d.balconies),
		parkings: sups.flatMap(d => d.parkings),
		builtUpAreas: sups.flatMap(d => d.builtUpAreas),
		facings: sups.flatMap(d => d.facings),
		floors: sups.flatMap(d => d.floors),
		prices: sups.flatMap(d => d.prices),
	};
}

/**
 * Creates lookup maps for property details, offers, and unread counts
 */
export function createLookupMaps(
	propertyDetails: PropertyDetails[],
	projectIds: string[],
	buyerOffers: Array<{ unitId?: string; supId?: string; review?: { price?: number; note?: string }; updatedAt?: Date }>,
	unreadCounts: Array<{ buyerUnitId: string; unreadByBuyer: number }>,
) {
	// Property details by ID
	const propertyDetailsMap = new Map<string, PropertyDetails>();
	propertyDetails.forEach(detail => propertyDetailsMap.set(detail._id, detail));

	// Merged project details
	const projectDetailsMap = new Map<string, PropertyDetails>();
	projectIds.forEach(projectId => {
		const sups = propertyDetails.filter(d => d.projectId === projectId && d.type === "sp");
		if (sups.length > 0) {
			projectDetailsMap.set(projectId, mergeProjectDetails(sups));
		}
	});

	// Offers by unit ID and SUP ID
	const offersMapByUnit = new Map<string, BuyerOffer>();
	const offersMapBySupId = new Map<string, BuyerOffer>();
	buyerOffers.forEach(offer => {
		const offerData = { price: offer.review?.price, note: offer.review?.note, updatedAt: offer.updatedAt };
		if (offer.unitId) offersMapByUnit.set(offer.unitId, offerData);
		if (offer.supId) offersMapBySupId.set(offer.supId, offerData);
	});

	// Unread counts by buyer unit ID
	const unreadCountsMap = new Map<string, number>();
	unreadCounts.forEach(item => unreadCountsMap.set(item.buyerUnitId, item.unreadByBuyer));

	return { propertyDetailsMap, projectDetailsMap, offersMapByUnit, offersMapBySupId, unreadCountsMap };
}

/**
 * Enhances a dashboard item with property details, offers, and unread status
 */
export function enhanceItem(item: DashboardItem, maps: ReturnType<typeof createLookupMaps>): DashboardItem {
	const { propertyDetailsMap, projectDetailsMap, offersMapByUnit, offersMapBySupId, unreadCountsMap } = maps;

	const unitId = getUnitId(item);
	const projectId = getProjectId(item);
	const type = (item as SuggestedProperty).type || (unitId ? "unit" : "project");
	const buyerUnitId = (item as BuyerUnit).id;

	let details: PropertyDetails | undefined;
	let offer: BuyerOffer | undefined;

	if (type === "unit" && unitId) {
		details = propertyDetailsMap.get(unitId);
		offer = offersMapByUnit.get(unitId) || offersMapBySupId.get(unitId);
	} else if (projectId) {
		const bpId = (item as BuyerProject).id;
		details = projectDetailsMap.get(projectId) || projectDetailsMap.get(bpId);
	}

	const hasNewOffer = buyerUnitId ? (unreadCountsMap.get(buyerUnitId) ?? 0) > 0 : false;

	return {
		...item,
		propertyDetails: details,
		buyerOffer: offer,
		hasNewOffer,
	};
}

/**
 * Creates empty categorized properties object
 */
export function createEmptyCategories(): CategorizedProperties {
	return {
		shortlisted: [],
		interested: [],
		visited: [],
		rejected: [],
		suggested: [],
	};
}
